import type { LocalizedString } from "../i18n";
import type { OrderAmountType } from "./portfolio-collections";

/**
 * Minimum-buy-per-instrument (QP-193). A product can carry a `minBuyAmount` —
 * the smallest allowed buy per event (the one-time buy, or each recurring
 * occurrence). Enforcement is gated by the portfolio collection's
 * `enforceMinBuyAmounts` setting.
 *
 * The violation rule lives here so the API (flow-completion engine + portfolio
 * payloads) and the web UI (inline warnings) share one definition, mirroring
 * `instrumentSyncLevel`.
 */

/**
 * QL-398 — which transaction of an account follow-up an entry judges. The
 * bank sees one order per instruction (a buy delta per row, one switch order
 * per pair) and applies the minimum to each on its own, so the gate judges
 * them one by one and never sums them per fund.
 */
export type MinBuyFollowupTransaction = {
  /**
   * "buy" = a fund not held (the full row is bought); "topup" = a held fund
   * (the row's buy delta); "switch" = one switch pair's estimated proceeds.
   */
  kind: "buy" | "topup" | "switch";
  /** Frozen matched holding of the product (0 when not held). */
  existingAmount: number;
  /** The pair judged (kind "switch"). */
  switchPairId?: string;
  /** The pair's source holding (kind "switch"). */
  sourcePositionId?: string;
};

/** One purchase below its product's configured minimum, as served on portfolio payloads. */
export type MinBuyViolation = {
  productId: string;
  productNameI18n: LocalizedString;
  /** The product's configured minimum buy per event. */
  minBuyAmount: number;
  /**
   * The judged purchase: the entered row amount (new advice, agreement
   * follow-ups), the derived buy (model / dynamic), or — on an account
   * follow-up — the top-up, the new buy or ONE switch's estimate, never the
   * target holding.
   */
  amount: number;
  /**
   * Model portfolios only: the portfolio-level amount needed for this product's
   * derived buy (amount × weight) to reach its minimum.
   */
  requiredPortfolioAmount?: number;
  /**
   * QL-398 — present only on an account follow-up's custom portfolio. One
   * entry per transaction, so a productId can repeat (two switches into one
   * fund) and can name a fund that has no custom row (a switch-only target).
   */
  followup?: MinBuyFollowupTransaction;
};

/**
 * Whether a buy amount breaches a product's minimum. Breach iff
 * `0 < amount < minBuyAmount` — exactly 0 always passes (the client simply
 * isn't buying the product), and so does any amount at/above the minimum.
 * Products without a configured minimum and "units" products never breach.
 */
export function isMinBuyViolation(
  amount: number | null | undefined,
  minBuyAmount: number | null | undefined,
  orderAmountType: OrderAmountType,
): boolean {
  if (orderAmountType === "units") return false;
  if (minBuyAmount == null || !(minBuyAmount > 0)) return false;
  const a = amount ?? 0;
  return a > 0 && a < minBuyAmount;
}

/**
 * Model portfolios: the portfolio-level amount at which a product with the
 * given weight (%) meets its minimum buy. Null when the weight is not positive.
 */
export function requiredAmountForMinBuy(minBuyAmount: number, weightPct: number): number | null {
  if (!(weightPct > 0)) return null;
  return minBuyAmount / (weightPct / 100);
}
